import { styled } from '@mui/material'
import dayjs from 'dayjs'
import { memo, useEffect, useState } from 'react'
import '../../../pure-js/libs/ArrayHelper'
import { capitalize } from '../../../pure-js/libs/Common'
import { getUserPaymentStatus, getVatFromBruttoPrice, generatePaymentId } from '../../../pure-js/libs/EnrollmentHelper'
import { getPeriodDisplayName } from '../../../pure-js/libs/PeriodHelper'
import {
  resolveAdministrationFeeAmount,
  shouldIncludeStoredAdministrationFee
} from '../../../pure-js/libs/PaymentHelper'
import {
  Client,
  Enrollment,
  EnrollmentWithPayment,
  PayFor,
  Payment,
  PaymentMethod,
  PriceDetails,
  VatData
} from '../../../pure-js/types/GrooverTypes'
import printIcon from '../../public/printIcon.svg'
import { BorderRadixes } from '../enums/BorderRadixes'
import { EIGHT_PX, FOUR_PX, SIX_PX, TWELVE_PX } from '../enums/Spacings'
import { useActivity } from '../hooks/useActivity'
import { State } from '../hooks/useAppState'
import { getClient } from '../libs/DBApiHandler'
import { formatGrooverText, getGrooverText } from '../libs/GrooverTextRepository'
import { getLocalizedActivityType } from '../utils/activityUtils'
import Box from './Box'
import GrooverText from './GrooverText'
import { useCurrency } from '../hooks/useCurrency'

type Props = {
  state: State
  obfuscatedEnrollment?: EnrollmentWithPayment
  onHandleDownloadPDF: (index: number) => void
}

type ReceiptUserData = {
  name: string
  ssn: string
  userPaymentStatus: 'paid' | 'notPaid' | 'partlyPaid' | 'unknown'
  priceDetails: PriceDetails[]
}

type ReceiptData = {
  userType: 'mainUser' | 'partnerUser'
  payment: Payment
  userData: ReceiptUserData[]
}

const renderReceipt = (
  state: State,
  client: Client,
  enrollment: EnrollmentWithPayment,
  receiptData: ReceiptData,
  index: number,
  onHandleDownloadPDF: (index: number) => void
) => {
  const currency = useCurrency(client)
  // Calculate total administration fee
  const totalFinalPriceOre = receiptData.userData.reduce(
    (sum, user) => sum + user.priceDetails.reduce((userSum, pd) => userSum + pd.finalPrice, 0),
    0
  )

  let totalAdministrationFee = 0

  if (shouldIncludeStoredAdministrationFee(totalFinalPriceOre, client)) {
    receiptData.userData.forEach((user) => {
      user.priceDetails.forEach((pd) => {
        if (pd.administrationFee) {
          totalAdministrationFee += pd.administrationFee
        }
      })
    })
  }

  // If no admin fees found in price details, calculate from payment method
  if (totalAdministrationFee === 0 && client && receiptData.payment) {
    // Map payment source to PaymentMethod enum (prefer source over paymentMethod string)
    // paymentMethod might be a payment provider ID (e.g., Stripe pm_xxx), not the enum
    let paymentMethod: PaymentMethod | undefined

    if (receiptData.payment.source) {
      // Map payment source to PaymentMethod enum
      const sourceToMethodMap: Record<string, PaymentMethod> = {
        stripe: PaymentMethod.Stripe,
        swish: PaymentMethod.Swish,
        usercredit: PaymentMethod.UserCredit,
        vipps: PaymentMethod.VippsMobile,
        manual: PaymentMethod.BankTransfer,
        fritidskortet: PaymentMethod.Fritidskortet
      }
      paymentMethod = sourceToMethodMap[receiptData.payment.source.toLowerCase()]
    } else if (receiptData.payment.paymentMethod) {
      // Try to use paymentMethod directly if it's already a PaymentMethod enum value
      // Check if it matches any PaymentMethod enum value
      const validPaymentMethods = Object.values(PaymentMethod)
      if (validPaymentMethods.includes(receiptData.payment.paymentMethod as PaymentMethod)) {
        paymentMethod = receiptData.payment.paymentMethod as PaymentMethod
      }
    }

    if (paymentMethod) {
      totalAdministrationFee = resolveAdministrationFeeAmount(paymentMethod, client, totalFinalPriceOre)
    }
  }

  // Calculate VAT groups based on payment scenarios
  // VAT should be calculated on the total including administration fee
  const calculateEnrollmentVATGroups = (enrollment: Enrollment): VatData => {
    const activities = enrollment.activities || []
    const vat: VatData = {}

    // Helper function to calculate VAT for a single price detail
    // Calculate VAT on finalPrice only (admin fee is per transaction, not per price detail)
    const calculateVatForPriceDetail = (pd: PriceDetails) => {
      const activityVat = activities.find((activity) => activity.id === pd.activityId)?.vat || 0
      if (!vat[activityVat]) vat[activityVat] = 0

      // Calculate VAT on finalPrice only (admin fee will be added separately)
      vat[activityVat] += getVatFromBruttoPrice(pd.finalPrice, activityVat) / 100
      vat[activityVat] = Math.round(vat[activityVat] * 100) / 100
    }

    // Calculate VAT from userData (on finalPrices only)
    receiptData.userData.forEach((user) => {
      user.priceDetails.forEach(calculateVatForPriceDetail)
    })

    // Add VAT on administration fee once (per transaction, not per price detail)
    // Admin fee's VAT rate is always client's default VAT rate
    if (totalAdministrationFee > 0 && client?.defaultVAT !== undefined) {
      const defaultVatPercentage = client.defaultVAT // This is a percentage (e.g., 25 for 25%)

      // Use percentage as key for grouping
      if (!vat[defaultVatPercentage]) vat[defaultVatPercentage] = 0

      // Calculate VAT: getVatFromBruttoPrice expects price in öre and VAT as percentage, returns VAT in öre
      // Then divide by 100 to convert to SEK (matching the pattern used for price details)
      vat[defaultVatPercentage] += getVatFromBruttoPrice(totalAdministrationFee, defaultVatPercentage) / 100
      vat[defaultVatPercentage] = Math.round(vat[defaultVatPercentage] * 100) / 100
    }

    return vat
  }

  const vatPaymentRowString = getGrooverText('componentsReceiptReceiptPaymentVat')
  const vatData = calculateEnrollmentVATGroups(enrollment)
  const paymentSource = receiptData.payment.source == 'manual' ? 'BG' : capitalize(receiptData.payment.source)
  const paymentId = generatePaymentId(enrollment, receiptData.payment)

  // hide VAT for partial payments
  const showVat = receiptData.userData.some((user) => ['notPaid', 'partlyPaid'].includes(user.userPaymentStatus))
    ? false
    : true

  return (
    <div>
      {index > 0 && (
        <DownloadButton fullWidth direction="row" align="center" justify="flex-end">
          <Box
            direction="row"
            align="center"
            gap={EIGHT_PX}
            bottom
            spacing={EIGHT_PX}
            onClick={() => onHandleDownloadPDF(index)}
          >
            <img src={printIcon} width={16} height={16} />
            <GrooverText textKey="registrationCtactaDownloadReceipt" />
          </Box>
        </DownloadButton>
      )}
      <ReceiptBox fullWidth fullPadding spacing={TWELVE_PX} key={index} id={`receipt-${index}`}>
        {/* Use paymentRef as primary, payment ID as fallback */}
        {(receiptData.payment.paymentRef || paymentId) && (
          <Box fullWidth bottom align="flex-end">
            <span style={{ fontSize: '0.7rem', wordBreak: 'break-all' }}>
              {receiptData.payment.paymentRef || paymentId}
            </span>
          </Box>
        )}
        {client && client.name && client.orgNo && client.address && (
          <ReceiverRow fullWidth bottom>
            <GrooverText textKey="componentsReceiptReceiptPaymentRecieverLabel" />
            <ReceiverValues gap={FOUR_PX}>
              <span>{client.name}</span>
              <span>{client.orgNo}</span>
              <span style={{ textAlign: 'right' }}>{client.address}</span>
            </ReceiverValues>
          </ReceiverRow>
        )}
        <Box fullWidth direction="row" justify="space-between" bottom spacing={TWELVE_PX}>
          <GrooverText textKey="componentsReceiptReceiptPaymentDateLabel" />
          <Box align="flex-end">
            <span>{dayjs(receiptData.payment.paymentDate || new Date().toISOString()).format('YYYY-MM-DD')}</span>
          </Box>
        </Box>
        <Box fullWidth direction="row" justify="space-between" bottom spacing={TWELVE_PX}>
          <GrooverText textKey="componentsReceiptReceiptPaymentPaymentMethodLabel" />
          <Box align="flex-end">
            <span>{paymentSource}</span>
          </Box>
        </Box>
        {receiptData.userData.map((user, index) => (
          <Box fullWidth direction="row" justify="space-between" bottom spacing={TWELVE_PX} key={index}>
            <GrooverText textKey="componentsReceiptReceiptPaymentCustomerLabel" />
            <Box align="flex-end">
              <span>{user.name}</span>
              <span>{user.ssn}</span>
            </Box>
          </Box>
        ))}
        <Box fullWidth bottom gap={FOUR_PX}>
          <GrooverText textKey="componentsReceiptReceiptPaymentDescriptionLabel" />

          {receiptData.userData.map((user, index) => (
            <Box fullWidth top spacing={EIGHT_PX} gap={FOUR_PX} key={index}>
              <span>{user.name}</span>
              {user.priceDetails.map((pd) => {
                const activity = enrollment.activities.find((a) => a.id === pd.activityId)
                const activityTypeLabel = activity?.type ? getLocalizedActivityType(activity.type, client) : undefined
                return (
                  <Box key={pd.activityId} fullWidth gap={FOUR_PX}>
                    {activityTypeLabel && activityTypeLabel !== '-' && (
                      <ActivityTypeLabel>{activityTypeLabel}</ActivityTypeLabel>
                    )}
                    <DescriptionBox fullWidth fullPadding spacing={SIX_PX}>
                      <span>
                        {activity
                          ? `${activity.name} - ${formatGrooverText(
                              getGrooverText('componentsReceiptActivityOccasions'),
                              [
                                activity.noOfOccasions,
                                getPeriodDisplayName(client.periods, activity.periodId, activity.period)
                              ]
                            )}`
                          : ''}
                      </span>
                    </DescriptionBox>
                  </Box>
                )
              })}
            </Box>
          ))}
        </Box>
        {/* Display total from payment amount (what was actually paid) */}
        {(() => {
          return (
            <>
              <Box fullWidth direction="row" justify="space-between" bottom spacing={FOUR_PX}>
                <GrooverText textKey="componentsReceiptReceiptPaymentTotal" />
                <Box align="flex-end">
                  <span>{`${String((receiptData.payment.totalAmount / 100).toFixed(2))} ${currency}`}</span>
                </Box>
              </Box>{' '}
              {/* Show administration fee breakdown if present */}
              {totalAdministrationFee > 0 && (
                <Box fullWidth direction="row" align="center" justify="space-between" bottom spacing={FOUR_PX}>
                  <span>
                    {client?.defaultVAT !== undefined
                      ? formatGrooverText(getGrooverText('componentsReceiptIncludesAdminFeeWithVat'), [
                          client.defaultVAT
                        ])
                      : getGrooverText('componentsReceiptIncludesAdminFee')}
                  </span>
                  <span>{`${String((totalAdministrationFee / 100).toFixed(2))} ${currency}`}</span>
                </Box>
              )}
            </>
          )
        })()}
        {showVat &&
          Object.keys(vatData)
            .sort((a, b) => parseInt(a) - parseInt(b))
            .map((vat) => (
              <Box key={vat} fullWidth direction="row" align="center" justify="space-between">
                <span>{formatGrooverText(vatPaymentRowString, [vat])}</span>
                <span>{vatData[vat].toFixed(2).toString() + ' ' + currency}</span>
              </Box>
            ))}
      </ReceiptBox>
    </div>
  )
}

const CourseRegistrationReceipt = ({ state, obfuscatedEnrollment, onHandleDownloadPDF }: Props) => {
  const urlActivityId = window.location.pathname.split('/')[2]
  const [client, setClient] = useState<Client | undefined>(undefined)

  const { data: activity } = useActivity(urlActivityId)

  useEffect(() => {
    const fetchClient = async () => {
      if (activity?.clientId) {
        const clientData = await getClient(activity.clientId)
        if (clientData) setClient(clientData)
      }
    }

    if (activity?.id) {
      fetchClient()
    }
  }, [activity?.clientId])

  const enrollment = state.obfuscatedEnrollment.id ? state.obfuscatedEnrollment : obfuscatedEnrollment

  if (!client || !enrollment || !enrollment.payments) {
    return null
  }

  // Create receipt data per payment
  const receiptsData: ReceiptData[] = enrollment.payments
    .filter((payment) => payment.type === 'pay')
    .map((payment) => {
      const userId = payment.userId
      let userDataList: ReceiptUserData[] = []

      if (!userId || userId === enrollment.mainUser.id) {
        // Payment for main user
        const userType = 'mainUser'
        const userPaymentStatus = getUserPaymentStatus(enrollment, PayFor.Main)

        userDataList.push({
          name: state.obfuscatedEnrollment?.mainUser?.nonObfuscatedName || state.name || '',
          ssn: state.mainUserSsn,
          userPaymentStatus: userPaymentStatus.status,
          priceDetails: enrollment.priceDetails.filter((pd) => pd.appliesTo === userType)
        })

        // check for scenario: mainUser paid, no partnerUser payments
        if (
          enrollment.partnerUser?.id &&
          enrollment.payments.filter((p) => p.userId === enrollment.partnerUser?.id).length == 0
        ) {
          const partnerPaymentStatus = getUserPaymentStatus(enrollment, PayFor.Partner)

          if (partnerPaymentStatus.status === 'paid') {
            userDataList.push({
              name: state.obfuscatedEnrollment?.partnerUser?.nonObfuscatedName || state.partnerName || '',
              ssn: state.partnerUserSsn,
              userPaymentStatus: partnerPaymentStatus.status,
              priceDetails: enrollment.priceDetails.filter((pd) => pd.appliesTo === 'partnerUser')
            })
          }
        }

        return {
          userType: userType,
          payment: payment,
          userData: userDataList
        }
      } else if (enrollment.partnerUser && userId === enrollment.partnerUser.id) {
        // Payment for partner user
        const userType = 'partnerUser'
        const partnerPaymentStatus = getUserPaymentStatus(enrollment, PayFor.Partner)

        userDataList.push({
          name: state.obfuscatedEnrollment?.partnerUser?.nonObfuscatedName || state.partnerName || '',
          ssn: state.partnerUserSsn,
          userPaymentStatus: partnerPaymentStatus.status,
          priceDetails: enrollment.priceDetails.filter((pd) => pd.appliesTo === userType)
        })

        return {
          userType: userType,
          payment: payment,
          userData: userDataList
        }
      } else {
        return null // is it possible?
      }
    })
    .filter((data): data is ReceiptData => data !== null) // Filter out nulls or empty arrays

  // Render a receipt structure for each payment scenario
  // The renderReceipt function might need adjustment if it expects only one user per call
  return (
    <>
      {receiptsData.map((receipt, index) => (
        <Box key={index} fullWidth style={{ marginBottom: receiptsData.length > 1 ? TWELVE_PX : 0 }}>
          {renderReceipt(state, client, enrollment, receipt, index, onHandleDownloadPDF)}
        </Box>
      ))}
    </>
  )
}

const ReceiptBox = styled(Box)`
  border: 1px solid #929292;
  border-radius: ${BorderRadixes.regular};
  box-shadow: 0px 0px 8px 0px rgba(0, 0, 0, 0.1);
`

const DescriptionBox = styled(Box)`
  background-color: #f9f9f9;
  border-radius: ${BorderRadixes.small};
`

const DownloadButton = styled(Box)`
  cursor: pointer;
`

const ReceiverRow = styled(Box)`
  flex-direction: row;
  justify-content: space-between;

  @media (max-width: 600px) {
    flex-direction: column;
    gap: 4px;
  }
`

const ReceiverValues = styled(Box)`
  align-items: flex-end;

  @media (max-width: 600px) {
    align-items: flex-start;
  }
`

const ActivityTypeLabel = styled('span')`
  font-size: 0.65rem;
  color: #888;
  text-transform: uppercase;
  letter-spacing: 0.06em;
`

export default memo(CourseRegistrationReceipt)
